import React, { useEffect, useState } from 'react';
import { Users, CreditCard, TrendingUp, Activity } from 'lucide-react';
import axios from 'axios';
import { config } from '../../../config';

interface UserLog {
    id: number;
    name: string;
    email: string;
    createdAt: string;
}

interface ActivityLog {
    id: number;
    description: string;
    user: string;
    date: string;
}

interface Stats {
    totalUsers: number;
    totalPlans: number;
    totalSubscriptions: number;
    recentUsers: UserLog[];
    recentActivity: ActivityLog[];
}

const AdminDashboard: React.FC = () => {
    const [stats, setStats] = useState<Stats | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchStats();
    }, []);

    const fetchStats = async () => {
        try {
            const response = await axios.get(`${config.API_BASE_URL}/platform/stats`, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
            });
            setStats(response.data);
        } catch (error) {
            console.error('Error fetching stats:', error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return <div className="flex items-center justify-center h-64 text-gray-500">Carregando painel...</div>;
    }

    const cards = [
        {
            title: 'Total de Usuários',
            value: stats?.totalUsers || 0,
            icon: Users,
            color: 'bg-blue-500',
        },
        {
            title: 'Assinaturas Ativas',
            value: stats?.totalSubscriptions || 0,
            icon: Activity,
            color: 'bg-[#0C85BA]',
        },
        {
            title: 'Planos Disponíveis',
            value: stats?.totalPlans || 0,
            icon: CreditCard,
            color: 'bg-indigo-500',
        },
        {
            title: 'Receita Est. (Mensal)',
            value: 'R$ 0,00',
            icon: TrendingUp,
            color: 'bg-purple-500',
        },
    ];

    return (
        <div>
            <h1 className="text-2xl font-bold text-gray-800 mb-6 font-display">Visão Geral</h1>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
                {cards.map((card, index) => (
                    <div key={index} className="bg-white rounded-xl shadow-sm p-6 flex items-center border border-gray-100 transition-hover hover:shadow-md">
                        <div className={`${card.color} p-4 rounded-lg text-white mr-4 shadow-lg`}>
                            <card.icon size={24} />
                        </div>
                        <div>
                            <p className="text-sm text-gray-500 font-medium">{card.title}</p>
                            <h3 className="text-2xl font-bold text-gray-800 tracking-tight">{card.value}</h3>
                        </div>
                    </div>
                ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
                    <h2 className="text-lg font-bold text-gray-800 mb-4">Atividades Recentes</h2>
                    <div className="space-y-4">
                        {stats?.recentActivity && stats.recentActivity.length > 0 ? (
                            stats.recentActivity.map((act) => (
                                <div key={act.id} className="flex items-center p-3 hover:bg-gray-50 rounded-lg transition-colors border border-transparent hover:border-gray-100">
                                    <div className="bg-blue-50 p-2 rounded-full text-blue-600 mr-3">
                                        <TrendingUp size={16} />
                                    </div>
                                    <div className="flex-1">
                                        <p className="text-sm font-medium text-gray-900">{act.description}</p>
                                        <p className="text-xs text-gray-500">{act.user} • {new Date(act.date).toLocaleString()}</p>
                                    </div>
                                </div>
                            ))
                        ) : (
                            <p className="text-gray-400 text-sm text-center py-8">Nenhuma atividade recente.</p>
                        )}
                    </div>
                </div>

                <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-100">
                    <h2 className="text-lg font-bold text-gray-800 mb-4">Novos Usuários</h2>
                    <div className="space-y-4">
                        {stats?.recentUsers && stats.recentUsers.length > 0 ? (
                            stats.recentUsers.map((user) => (
                                <div key={user.id} className="flex items-center justify-between p-3 hover:bg-gray-50 rounded-lg transition-colors">
                                    <div className="flex items-center">
                                        <div className="h-8 w-8 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center font-bold mr-3 text-xs">
                                            {user.name.charAt(0).toUpperCase()}
                                        </div>
                                        <div>
                                            <p className="text-sm font-medium text-gray-900">{user.name}</p>
                                            <p className="text-xs text-gray-500">{user.email}</p>
                                        </div>
                                    </div>
                                    <span className="text-xs text-gray-400">{new Date(user.createdAt).toLocaleDateString()}</span>
                                </div>
                            ))
                        ) : (
                            <p className="text-gray-400 text-sm text-center py-8">Nenhum usuário novo hoje.</p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminDashboard;
